import AutoSuggest, { type SelectedSuggestion, type AutoSuggestSuggestion } from '@rio-cloud/rio-uikit/AutoSuggest';
import Spinner from '@rio-cloud/rio-uikit/Spinner';
import classNames from 'classnames';
import { type MouseEvent, type KeyboardEvent, type SyntheticEvent, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { isNullOrEmpty } from '../../../actions/helper';
import type { CountryCode } from './types';
import { getCountryCodes } from './utils/countryCodeUtils';

type CountryCodeAutoSuggestProperties = CountryCodeAutoSuggestOwnProperties;

interface CountryCodeAutoSuggestOwnProperties {
    assetId: string | null;
    onSelectionChange: (countryCode: string | null) => void;
    licensePlateCountryCode: string | null;
    locale: string;
    hasError: boolean;
}

export const CountryCodeAutoSuggest = (props: CountryCodeAutoSuggestProperties) => {
    const { assetId, licensePlateCountryCode, onSelectionChange, locale, hasError } = props;

    const [countryCodeEntries, setCountryCodeEntries] = useState<CountryCode[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    // Load country codes when locale changes
    useEffect(() => {
        let isCancelled = false;

        const loadCountryCodes = async () => {
            setIsLoading(true);
            try {
                const codes = await getCountryCodes(locale);
                if (!isCancelled) {
                    setCountryCodeEntries(codes);
                }
            } catch (error) {
                console.error('Failed to load country codes:', error);
                if (!isCancelled) {
                    setCountryCodeEntries([]);
                }
            } finally {
                if (!isCancelled) {
                    setIsLoading(false);
                }
            }
        };

        loadCountryCodes().catch(console.error);

        return () => {
            isCancelled = true;
        };
    }, [locale]);

    const initialDisplayValue = useMemo(() => {
        if (!licensePlateCountryCode || countryCodeEntries.length === 0) {
            return '';
        }
        return formatCountryCodeForDisplay(getCountryCodeFromCode(licensePlateCountryCode));
    }, [licensePlateCountryCode, countryCodeEntries]);

    const noItemFoundMessage = '...';
    const [suggestions, setSuggestions] = useState<CountryCode[]>([]);
    const [displayValue, setDisplayValue] = useState<string>('');

    // Update suggestions and display value when country codes or initial value changes
    useEffect(() => {
        if (countryCodeEntries.length > 0) {
            setSuggestions(countryCodeEntries);
            setDisplayValue(initialDisplayValue);
        }
    }, [countryCodeEntries, licensePlateCountryCode, initialDisplayValue, assetId]);

    function getCountryCodeFromCode(countryCode: string | null) {
        if (!countryCode) {
            return undefined;
        }
        return countryCodeEntries.find(entry => countryCode.toLowerCase() === entry.code?.toLowerCase());
    }

    function getCountryCodesFromSearchValue(label: string | null | undefined) {
        return countryCodeEntries.filter(entry =>
            isNullOrEmpty(label)
                ? true
                : entry.code?.toLowerCase().startsWith(label.toLowerCase()) ||
                  entry.label?.toLowerCase().startsWith(label.toLowerCase())
        );
    }

    const getSuggestionValue = (countryCode: AutoSuggestSuggestion) => {
        onSelectionChange(countryCode.code as string);
        return formatCountryCodeForDisplay(countryCode);
    };

    const renderSuggestion = (autoSuggestSuggestion: AutoSuggestSuggestion) => {
        return <span>{formatCountryCodeForDisplay(autoSuggestSuggestion)}</span>;
    };

    function displayValueMatchesCountryLabelAndCode(value: string) {
        return countryCodeEntries.find(entry => value === `${entry.label} (${entry.code})`);
    }

    function displayValueMatchesCountryCode(value: string) {
        return countryCodeEntries.find(entry => value === `${entry.code}`);
    }

    const onSuggestionsFetchRequested = (argument: { value: string }) => {
        setSuggestions(countryCodeEntries);
        if (isNullOrEmpty(argument.value)) {
            // call to handleClear internally when value empty
            onSelectionChange(null);
        } else if (displayValueMatchesCountryLabelAndCode(argument.value)) {
            // call to handleChange internally when change in value
            onSelectionChange(argument.value.split('(')[1].substring(0, 2));
        } else if (displayValueMatchesCountryCode(argument.value)) {
            onSelectionChange(argument.value);
        }
    };

    const onSuggestionSelected = (
        _event: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>,
        selectedSuggestion: SelectedSuggestion
    ) => {
        const countryCode = selectedSuggestion.suggestion as unknown as CountryCode;
        const countryCodeFromCode = getCountryCodeFromCode(countryCode.code);
        if (countryCodeFromCode != null) {
            onSelectionChange(countryCode.code);
        }
    };

    const handleChange = (_changeEvent: SyntheticEvent, { newValue }: { newValue: string }) => {
        setDisplayValue(newValue);
        setSuggestions(getCountryCodesFromSearchValue(newValue));
    };

    const handleClear = () => {
        setDisplayValue('');
        setSuggestions(countryCodeEntries);
    };

    const inputProperties = {
        onChange: handleChange,
        onClear: handleClear,
        placeholder: '',
        value: displayValue,
        disabled: isLoading,
        hasError,
    };

    return (
        <div className={classNames('form-group')} data-cy={'detail-view-form-license-plate-country-code'}>
            <label htmlFor={'CountryCodeAutoSuggest'}>
                <FormattedMessage id={'assets.assets.asset.country-of-registration'} />
            </label>
            <div className={'position-relative'}>
                <AutoSuggest
                    id={'CountryCodeAutoSuggest'}
                    inputProps={{ ...inputProperties }}
                    suggestions={suggestions}
                    noItemMessage={noItemFoundMessage}
                    onSuggestionsFetchRequested={onSuggestionsFetchRequested}
                    onSuggestionSelected={onSuggestionSelected}
                    renderSuggestion={renderSuggestion}
                    getSuggestionValue={getSuggestionValue}
                />
                {isLoading && (
                    <div className={'position-absolute top-25pct left-50pct'}>
                        <Spinner isInline={true} />
                    </div>
                )}
            </div>
        </div>
    );
};

const formatCountryCodeForDisplay = (countryCode: CountryCode | AutoSuggestSuggestion | undefined) => {
    if (countryCode !== undefined && !!countryCode?.label && !!countryCode?.code) {
        return `${countryCode?.label} (${countryCode?.code})`;
    } else {
        return '';
    }
};
